import {
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ICellRendererAngularComp } from 'ag-grid-angular';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { getNestedProperty } from 'src/app/shared/utils/object-utils';

export interface AgActionsMenuAction {
  label: string;
  icon?: IconProp;
  action: (data: any) => void;
  disabled?: (data: any) => boolean;
  hidden?: (data: any) => boolean;
}

/** A divider between groups of actions (WAP-1745); dropped when it would lead, trail or double up. */
export interface AgActionsMenuSeparator {
  separator: true;
}

export type AgActionsMenuItem = AgActionsMenuAction | AgActionsMenuSeparator;

export function isActionsMenuSeparator(
  item: AgActionsMenuItem,
): item is AgActionsMenuSeparator {
  return 'separator' in item;
}

/**
 * `cellRendererParams` of the actions-menu renderer.
 * - `titleKey`: row field shown as the cell text (defaults to the cell value)
 * - `items`: the hamburger menu entries
 * - `titleAction`: when set, the title renders as a link (same affordance as
 *   the data-object renderer) and clicking it runs the action with the row -
 *   the row's primary destination, so it is not buried in the menu
 */
export interface AgActionsMenuCellRendererParams {
  titleKey?: string;
  items?: AgActionsMenuItem[];
  titleAction?: (data: any) => void;
}

@Component({
  selector: 'app-ag-actions-menu-cell-renderer',
  templateUrl: './ag-actions-menu-cell-renderer.component.html',
  styleUrl: './ag-actions-menu-cell-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgActionsMenuCellRendererComponent implements ICellRendererAngularComp {
  params: any;
  title: string;
  items: AgActionsMenuItem[] = [];
  titleAction: ((data: any) => void) | null = null;
  dropdownVisible = false;

  constructor(private cdr: ChangeDetectorRef) {}

  agInit(params: any): void {
    this.setParams(params);
  }

  refresh(params: any): boolean {
    this.setParams(params);
    this.cdr.detectChanges();
    return true;
  }

  private setParams(params: any): void {
    this.params = params;
    const titleKey = params.colDef?.cellRendererParams?.titleKey;
    this.title =
      titleKey != null
        ? getNestedProperty(params.data, titleKey, null)
        : (params.valueFormatted ?? params.value);
    this.items = params.colDef?.cellRendererParams?.items ?? [];
    this.titleAction =
      (params.colDef?.cellRendererParams as AgActionsMenuCellRendererParams)
        ?.titleAction ?? null;
  }

  onTitleClick(event: Event): void {
    if (!this.titleAction || !this.params?.data) {
      return;
    }
    event.stopPropagation();
    this.titleAction(this.params.data);
  }

  visibleItems(): AgActionsMenuItem[] {
    const visible = this.items.filter(
      (i) => isActionsMenuSeparator(i) || !i.hidden?.(this.params.data),
    );
    return visible.filter(
      (i, idx) =>
        !isActionsMenuSeparator(i) ||
        (idx > 0 &&
          idx < visible.length - 1 &&
          !isActionsMenuSeparator(visible[idx - 1])),
    );
  }

  isSeparator(item: AgActionsMenuItem): item is AgActionsMenuSeparator {
    return isActionsMenuSeparator(item);
  }

  onItemClick(item: AgActionsMenuAction, event: MouseEvent): void {
    event.stopPropagation();
    if (item.disabled?.(this.params.data)) {
      return;
    }
    item.action(this.params.data);
    this.dropdownVisible = false;
  }

  isDisabled(item: AgActionsMenuAction): boolean {
    return !!item.disabled?.(this.params.data);
  }

  onToggle(open: boolean): void {
    this.dropdownVisible = open;
  }
}
